import { Global } from '@emotion/react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { Grid, IconButton, Stack, useMediaQuery } from '@mui/material';
import { ExpandLessOutlined } from '@mui/icons-material';
import { useTheme, styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import EirCartPriceSummary from './EirCartPriceSummary';
// import EirCartButtons from './EirCartButtons';
import styles from './styles';
import EirCartDetails from './EirCartDetails';
import { getCartDetails, getTacticalOfferDetails } from './cart.util';
import useLocalStorage, { getLocalStorage } from '../../hooks/useLocalStorage';
import RootContext from '../../../util/root.context';
import { viewCartAnalytics } from '../../../util/gtm';
import cmsData from '../../../util/cmsData.util';
import { getTokenFromCookies } from '../../hooks/useCookie';
import { removeTariff } from '../eir-broadband/Broadband.util';

const drawerBleeding = 96;

const Root = styled('div')(({ theme }) => ({
  background: theme.palette.secondary.contrastText,
  bottom: 0,
  position: 'fixed',
  width: '100%',
  height: 'auto',
  zIndex: 1200,
  left: 0,
  right: 0,
  borderTopLeftRadius: '0.625rem',
  borderTopRightRadius: '0.625rem',
  [theme.breakpoints.down('sm')]: {
    borderTopLeftRadius: '0.625rem',
    borderTopRightRadius: '0.625rem',
  },
}));

const StyledBox = styled(Box)(() => ({
  // backgroundColor: theme.palette.mode === 'light' ? '#fff' : grey[800],
}));

const announce = message => {
  const existing = document.getElementById('eir-cart-announcer');
  if (existing) existing.remove();

  setTimeout(() => {
    const el = document.createElement('div');
    el.id = 'eir-cart-announcer';
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-roledescription', ' '); // Suppresses "alert" prefix
    el.setAttribute('aria-atomic', 'true');
    el.style.cssText =
      'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;';
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  }, 300);
};
const EirCartContainer = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [cartData, setCartData] = useState({});
  const [tacticalOffers, setTacticalOffers] = useState([]);
  const graphql = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [deleteIcon] = useState(false);
  const summaryData = cmsData();
  const localStore = useLocalStorage();
  const isCartDoc = true;

  const { state, dispatch } = useContext(RootContext);

  const toggleDrawer = newOpen => () => {
    setOpen(newOpen);
    announce(newOpen ? 'button expanded' : 'button collapsed');
    if (newOpen) viewCartAnalytics('bottomCart', state.cart);
  };

  const getCart = async () => {
    let data = getCartDetails(state.cart);
    if (getLocalStorage('HANDSET_BE_OFFERID') && data?.cart?.length > 1) {
      const offerId = parseInt(getLocalStorage('HANDSET_BE_OFFERID'), 10);
      const result = await removeTariff(graphql, offerId);
      if (result) {
        localStore.removeLocalStorage('HANDSET_BE_OFFERID');
        dispatch({ type: 'SET_CART', payload: result });
        data = getCartDetails(result);
      }
    }
    setLoading(true);
    setCartData(data);

    /* Hide tactical offers for existing customers */
    if (!getTokenFromCookies()) {
      if (tacticalOffers.length === 0 && data?.hasBroadband === true)
        setTacticalOffers(await getTacticalOfferDetails(graphql));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (localStore.hasValue('Prospectuuid')) {
      // dispatch({ type: 'SHOW_CART', payload: true });
    }
  }, [state.cart, state.updateContext]);

  useEffect(() => {
    if (state.enableCart) {
      getCart();
    }
  }, [state.updateContext]);

  return (
    <Root>
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: 'auto',
            overflow: 'visible',
            backgroundColor: sm ? 'transparent' : '',
            boxShadow: sm && 'none',
          },
        }}
      />

      <Stack
        direction={sm ? 'column-reverse' : 'row'}
        justifyContent='flex-end'
        alignItems='center'
        sx={
          state.enableCart
            ? {
                ...styles(theme).cartSummary,
                boxShadow: `0px 4px 4px ${theme.palette.primary.boxShadow4Lite}, 0px -6px 6px ${theme.palette.primary.boxShadow2Dark}, 0px -10px 14px ${theme.palette.primary.boxShadow2Medium}`,
              }
            : { display: 'none' }
        }
      >
        {!open && (
          <Grid
            container
            justifyContent={!sm ? 'flex-end' : 'center'}
            sx={styles(theme).priceDetails}
          >
            <EirCartPriceSummary cartData={cartData} loading={loading} />
            {/* <EirCartButtons cta={[{ text: 'Checkout ' }, { text: 'Next' }]} /> */}
          </Grid>
        )}

        {!open && (
          <IconButton aria-expanded={false} onClick={toggleDrawer(true)}>
            <ExpandLessOutlined
              size='large'
              color={theme.palette.primary.buttonColor}
              sx={styles(theme).expandMoreIcon}
            />
          </IconButton>
        )}
        <SwipeableDrawer
          anchor='bottom'
          open={open}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
          swipeAreaWidth={drawerBleeding}
          disableSwipeToOpen
          sx={styles(theme).swipeDrawer}
        >
          {open && (
            <StyledBox sx={styles(theme).boxDrawerBg}>
              <Stack direction={sm ? 'column-reverse' : 'row'} sx={styles(theme).boxColor}>
                <EirCartDetails
                  cartData={cartData}
                  loading={loading}
                  deleteIcon={deleteIcon}
                  missingStep
                  summaryData={summaryData}
                  tacticalOffers={tacticalOffers}
                  isCartDoc={isCartDoc}
                />

                <IconButton
                  aria-hidden='true'
                  onFocus={() => announce('button expanded')}
                  onClick={toggleDrawer(false)}
                >
                  <ExpandMoreIcon
                    size='large'
                    color={theme.palette.primary.buttonColor}
                    sx={styles(theme).expandIcon}
                  />
                </IconButton>
              </Stack>
              <Stack
                direction={sm ? 'column' : 'row'}
                justifyContent='center'
                alignItems='center'
                sx={styles(theme).bottomCart}
              >
                {!sm &&
                  (cartData.offerType !== 'PREPAY' ? (
                    <EirCartPriceSummary cartData={cartData} loading={loading} />
                  ) : null)}

                {/* <EirCartButtons cta={[{ text: 'Checkout ' }, { text: 'Next' }]} /> */}
              </Stack>
            </StyledBox>
          )}
        </SwipeableDrawer>
      </Stack>
    </Root>
  );
};
export default EirCartContainer;
